// Extraído standalone de packages/hce/src/hce/QuestionnaireEngine/extractors.ts
// (función `construirReceta`) — en hce-chart no existe el motor de
// Questionnaire genérico, así que esta función vive sola en vez de dentro de
// un archivo de "extractores" del motor. Ver el comentario original: la
// receta tiene su propio wizard con firma electrónica entre medio
// (RecetaWizard.tsx, quien la invoca), así que nunca fue un `Extractor`
// genérico — es solo una función de armado del recurso FHIR.
//
// 005 — Evolución y Prescripciones, sub-flujo de receta: un `MedicationRequest`
// por medicamento (AM 1124 Art. 5/7.II.10 — nunca varias líneas en un solo
// documento), DCI primero con el nombre comercial como dato secundario entre
// paréntesis, "pauta" (dosis/frecuencia/duración) como campo separado en
// `dosageInstruction` (Art. 7.II.11).
import type { MedplumClient } from '@medplum/core';
import type { DocumentReference, Encounter, MedicationRequest, Patient, Reference } from '@medplum/fhirtypes';
import { extensionesReceta } from './extensiones';
import { reasonReferenceDeCondicion } from './reasonReference';

export interface ConstruirRecetaInput {
  medplum: MedplumClient;
  subject: Reference<Patient>;
  encounter?: Reference<Encounter>;
  dci: string;
  nombreComercial?: string;
  formaFarmaceutica?: string;
  /** "1 tableta cada 8 horas por 7 días" — ya armado por el wizard, campo separado del nombre del medicamento. */
  pauta?: string;
  /** Cantidad total a dispensar — el wizard la calcula desde dosis/frecuencia/duración, editable antes de firmar. */
  cantidadTotal?: number;
  unidadCantidad?: string;
  sustanciaControlada: boolean;
  /** Homologación manual con el papel de ACESS (2.8, ruta 1) — solo cuando `sustanciaControlada`. */
  numeroSerieRecetaEspecial?: string;
  /** `DocumentReference` del PDF de la receta firmado por Security Data (o el adaptador simulado). */
  documentReferenceFirma: Reference<DocumentReference>;
  /** `Condition` que justifica la receta para auditorías de glosa — RecetaWizard.tsx todavía no lo pasa, no existe selector de UI que lo capture (queda listo para cuando exista). */
  conditionId?: string;
}

export async function construirReceta(input: ConstruirRecetaInput): Promise<MedicationRequest> {
  const { medplum, subject, encounter, dci, nombreComercial, formaFarmaceutica, pauta, cantidadTotal, unidadCantidad, documentReferenceFirma, conditionId } = input;
  // Sin `concentracion`: el pedido del médico (Art. 5.c.iii de la Resolución
  // ACESS-2023-0030) ya queda cubierto por la fuerza (mg) que trae el nombre
  // del producto del catálogo ARCSA — el texto de composición completo del
  // campo `concentracion` (excipientes, porcentajes, a veces con caracteres
  // corruptos del propio registro de ARCSA) es ruido redundante, no un dato
  // legal adicional.
  const nombreMedicamento = [dci.trim(), nombreComercial?.trim() ? `(${nombreComercial.trim()})` : '', formaFarmaceutica?.trim()]
    .filter(Boolean)
    .join(' ');

  return medplum.createResource<MedicationRequest>({
    resourceType: 'MedicationRequest',
    status: 'active',
    intent: 'order',
    subject,
    encounter,
    authoredOn: new Date().toISOString(),
    medicationCodeableConcept: { text: nombreMedicamento },
    dosageInstruction: pauta ? [{ text: pauta }] : undefined,
    dispenseRequest: cantidadTotal !== undefined ? { quantity: { value: cantidadTotal, unit: unidadCantidad } } : undefined,
    supportingInformation: [documentReferenceFirma],
    extension: extensionesReceta({ sustanciaControlada: input.sustanciaControlada, numeroSerieRecetaEspecial: input.numeroSerieRecetaEspecial }),
    reasonReference: reasonReferenceDeCondicion(conditionId),
  });
}
